import type { Invoice } from '@/store/expenses-store/invoices/invoices.dto';
import type {
  Transaction,
  TransactionCategory,
  TransactionRow,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import type { SingleServiceProvider } from '@/store/service-providers/service-providers.dto';

import { crewRecipientLabel, crewRecipientOf } from './crewRecipient';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const transformExpenseToRow = (
  expense: Transaction,
  serviceProvidersMap?: Map<string, SingleServiceProvider>,
  invoicesMap?: Map<string, Invoice>,
  categories?: TransactionCategory[],
): TransactionRow => {
  const rawCategory = expense.expenses_category?.trim() ?? '';
  const backendCat = rawCategory
    ? categories?.find((c) => c.id === rawCategory || c.slug === rawCategory)
    : undefined;
  const categoryTitle = (backendCat?.title ?? rawCategory) || 'Other';
  const categoryIcon = backendCat?.icon;
  const categoryColor = backendCat?.color ?? '#94A3B8';

  const recipientId =
    typeof expense.recipient === 'string' ? expense.recipient : (expense.recipient?.id ?? '');
  const recipientName =
    typeof expense.recipient === 'string' ? undefined : (expense.recipient?.name ?? undefined);
  const serviceProvider = recipientId ? serviceProvidersMap?.get(recipientId) : undefined;
  // A crew member reads from the snapshot saved with the transaction, so it still names them
  // after they leave the vessel.
  const crewRecipient = crewRecipientOf(expense);
  const recipient = {
    id: recipientId || undefined,
    name: crewRecipient
      ? crewRecipientLabel(crewRecipient)
      : (serviceProvider?.name ?? recipientName ?? recipientId),
  };

  const docs: { fileName?: string } | null = expense.receipt ? { fileName: expense.receipt } : null;

  let invoiceName: string | null = null;
  if (expense.invoice_id) {
    const invoice = invoicesMap?.get(expense.invoice_id);
    if (invoice) {
      invoiceName = invoice.invoice_number ?? null;
    }
  }

  return {
    id: expense.id,
    icon: 'icon',
    type: categoryTitle,
    categoryId: backendCat?.id,
    categoryIcon,
    categoryColor,
    date: formatDate(expense.created_at),
    createdAt: expense.created_at ?? null,
    reason:
      // expense.description?.trim() ||
      invoiceName
        ? `Invoice #${invoiceName}`
        : expense.invoice_id
          ? `Invoice #${expense.invoice_id}`
          : recipient.name || 'No description',
    recipient,
    transactionType: expense.type,
    status: expense.status,
    method: expense.method,
    amount: parseFloat((expense.amount?.number as string) || '0'),
    docs,
    description: expense.description ?? undefined,
    paymentLink: expense.payment_link ?? undefined,
  };
};

export const transformExpensesToRows = (
  expenses: Transaction[],
  serviceProvidersMap?: Map<string, SingleServiceProvider>,
  invoicesMap?: Map<string, Invoice>,
  categories?: TransactionCategory[],
): TransactionRow[] => {
  if (!Array.isArray(expenses)) return [];
  return expenses.map((expense) =>
    transformExpenseToRow(expense, serviceProvidersMap, invoicesMap, categories),
  );
};

/** Who a row's money went to, as the list prints it; '' when the transaction names no one. */
export const recipientName = ({ recipient }: Pick<TransactionRow, 'recipient'>): string =>
  typeof recipient === 'string' ? '' : (recipient?.name ?? '');
